/**
 * **The Pomiary tab's vocabulary** — what a body measurement IS here: the snapshot shape, the
 * eleven quantities the tab reads, the direction each one is good in, and the nine fields this app
 * can write. What the tab DRAWS is `model/twoDates`, which is the entry point.
 *
 * ── Every series comes from the HISTORY, and that is a contract gap made visible ───────────────
 * `GET /customer/{id}/body` publishes a weekly `trend` carrying **weight and body fat only**, while
 * every snapshot carries eleven quantities. `GET /customer/{id}/body/history` returns those full
 * snapshots, each with its own `recordedAt`, which is the honest source for all eleven. The
 * consequence is deliberate: the x axis is drawn from real measurement dates rather than from even
 * weekly steps, so a fortnight without a measurement reads as a gap.
 *
 * ── The form is validated here and nowhere else ────────────────────────────────────────────────
 * `buildMeasurementRequest` returns either a request or the fields that refused it. The bounds are
 * the backend's own (`Domain/BodyMeasurementValidation.cs`), so a value this app accepts is one the
 * server accepts, and the trainer never learns about a bound from a 400.
 */

import type { TrainWithMeModulesClientsDtosRequestsPostBodyMeasurementRequest as PostBodyMeasurementRequest } from '~/shared/api/generated/model';

/**
 * The `source` a trainer's own entry carries. The backend forces a customer's own submission to
 * `SelfReport` regardless of what is sent, so this is the only value this app ever writes.
 */
const TRAINER_SOURCE = 'TrainerMeasure';

/**
 * Whether a snapshot's wire `source` says a TRAINER recorded it — the same one comparison the tab
 * made before 18 September 2026, restored rather than widened. Anything else is the client's own
 * reading, which is the safe side to land an unknown value on: claiming a trainer measured
 * something nobody can name is the worse of the two mistakes.
 */
export function isTrainerSource(source: string | null | undefined): boolean {
  return source === TRAINER_SOURCE;
}

/**
 * One stored measurement, narrowed at the API seam.
 *
 * `recordedText` (the backend's own relative wording) and `source` came through here while the tab
 * drew a history list with a date and a source beside every row. The 18 September canon draws
 * neither, so neither is narrowed any more — a field nothing reads is a claim nobody checks. Both
 * are still on the wire and cost one line each to bring back.
 */
export interface MeasurementSnapshot {
  readonly id: number;
  readonly recordedAt: string;
  /**
   * Whether a TRAINER recorded this snapshot, rather than the client reporting it themselves.
   *
   * A narrow boolean rather than the wire's `source` string, because the two things this app does
   * with it are both binary: the canon draws a trainer's reading as a FILLED dot and a client's
   * check-in as a hollow one, and the hover readout names which of the two it was. The wording
   * comes from `clients.detail.body.source.*`, which the copied locale already carries for both.
   */
  readonly byTrainer: boolean;
  readonly weightKg: number;
  readonly bodyFatPercentage: number | null;
  readonly waistCm: number | null;
  readonly hipCm: number | null;
  readonly chestCm: number | null;
  readonly neckCm: number | null;
  readonly bicepCm: number | null;
  readonly thighCm: number | null;
  readonly calfCm: number | null;
  readonly shoulderCm: number | null;
  readonly forearmCm: number | null;
}

/**
 * **All ELEVEN quantities a snapshot carries**, in the canon's own order — which is the order the
 * comparison table prints and the order the "Linia" switch offers.
 *
 * It was five until 18 September 2026. The owner's note on the shipped tab: "wykres miał 160px i
 * rysował jeden z pięciu parametrów, a każdy pomiar niesie jedenaście". Nothing about the read
 * changed — `/body/history` always answered with all eleven.
 */
export const MEASUREMENT_PARAMETERS = [
  'weightKg',
  'bodyFatPercentage',
  'chestCm',
  'waistCm',
  'hipCm',
  'shoulderCm',
  'neckCm',
  'bicepCm',
  'forearmCm',
  'thighCm',
  'calfCm',
] as const;

export type MeasurementParameter = (typeof MEASUREMENT_PARAMETERS)[number];

/**
 * **The ELEVEN the "Linia" switch offers**, in the order the pill reads them.
 *
 * Owner, 25 September 2026: the switch offered six and the trainer could not chart the other five
 * ("pomiary nie dają możliwości wyboru wszystkich partii"). Every quantity the table prints is now
 * chartable. The control is still ONE segmented pill, never a row of bordered chips (19 September);
 * where the card is too narrow for eleven options the pill scrolls sideways rather than wrapping.
 *
 * The ORDER differs from the table's: waist and hips come before the chest, because a reduction's
 * two headline girths belong beside body fat, and the canon's first six keep their places. The five
 * added on 25 September follow in the table's own order.
 */
export const MEASUREMENT_CHART_PARAMETERS = [
  'weightKg',
  'bodyFatPercentage',
  'waistCm',
  'hipCm',
  'chestCm',
  'thighCm',
  'shoulderCm',
  'neckCm',
  'bicepCm',
  'forearmCm',
  'calfCm',
] as const satisfies readonly MeasurementParameter[];

/**
 * The four quantities a FALL is good news for, and the only four anything is ever coloured by.
 *
 * Weight is deliberately not one of them: a client on a bulk and a client on a cut want opposite
 * directions of the same number, and the tab has no way of knowing which. The canon says the same
 * thing in its own note — "zielony oznacza zmianę tylko tam, gdzie celem jest mniej".
 */
const LOWER_IS_THE_GOAL: ReadonlySet<string> = new Set([
  'waistCm',
  'hipCm',
  'bodyFatPercentage',
  'neckCm',
]);

/** Whether a change of `delta` in `field` is the direction the trainer wanted. */
export function isGoodDirection(field: string, delta: number): boolean {
  return delta < 0 && LOWER_IS_THE_GOAL.has(field);
}

export interface MeasurementPoint {
  readonly x: Date;
  readonly y: number;
}

/** The unit each parameter carries. Weight and fat differ; every circumference is centimetres. */
export function measurementUnitKey(field: string): string {
  if (field === 'weightKg') return 'clients.detail.body.unitKg';
  if (field === 'bodyFatPercentage') return 'clients.detail.body.unitPct';
  return 'web:clientDetail.body.unitCm';
}

/**
 * The copied locale's own label table, which mobile's own form and survey both read — with ONE
 * override, and the override is why these two functions exist at all.
 *
 * Mobile abbreviates body fat to „Tkanka tł." because a phone column cannot hold the word. This
 * app's comparison table has 150px for a label and the canon prints „Tkanka tłuszczowa" whole,
 * while the segmented pill prints the first word alone, „Tkanka". So exactly one quantity carries
 * two web keys and the other ten come from the copied table unchanged — a parallel table of eleven
 * strings to change one of them is the duplication this repo's own rule refuses.
 */
const WEB_LABEL_OVERRIDE: ReadonlySet<string> = new Set(['bodyFatPercentage']);

/** The label a quantity wears in full: the table's rows, the stage's reading, the drawer's field. */
export function measurementLabelKey(field: string): string {
  return WEB_LABEL_OVERRIDE.has(field)
    ? `web:clientDetail.body.quantity.${field}`
    : `clients.detail.surveys.measure.${field}`;
}

/** The label the segmented pill wears, where one word has to stand for the quantity. */
export function measurementShortLabelKey(field: string): string {
  return WEB_LABEL_OVERRIDE.has(field)
    ? `web:clientDetail.body.short.${field}`
    : `clients.detail.surveys.measure.${field}`;
}

/* ─────────────────────────────── the form ─────────────────────────────── */

/**
 * The form's own field names, which are NOT the wire's — `hips` is `hipCm`, `biceps` is `bicepCm`.
 *
 * **NINE since 18 September 2026, in the drawer's own reading order**: the two that describe mass,
 * then the seven girths. `neck` and `calf` joined the seven this app used to send because the
 * drawer draws the same nine the MOBILE app writes, and `POST /customer/{id}/body` has always
 * accepted `neckCm` and `calfCm`. Nothing on the wire changed.
 *
 * `shoulderCm` and `forearmCm` stay readable and unwritable: mobile does not offer them either, and
 * the owner's instruction was the nine mobile has, not eleven.
 */
export const MEASUREMENT_FORM_FIELDS = [
  'weight',
  'bodyFat',
  'neck',
  'chest',
  'biceps',
  'waist',
  'hips',
  'thigh',
  'calf',
] as const;

export type MeasurementFormField = (typeof MEASUREMENT_FORM_FIELDS)[number];

export type MeasurementFormValues = Readonly<Record<MeasurementFormField, string>>;

export const EMPTY_MEASUREMENT_FORM: MeasurementFormValues = {
  weight: '',
  bodyFat: '',
  neck: '',
  chest: '',
  biceps: '',
  waist: '',
  hips: '',
  thigh: '',
  calf: '',
};

export interface MeasurementBounds {
  readonly min: number;
  readonly max: number;
}

/** The backend's own bounds (`HumanBodyBounds`), inclusive. A value inside them cannot 400. */
const BOUNDS: Readonly<Record<MeasurementFormField, MeasurementBounds>> = {
  weight: { min: 20, max: 500 },
  bodyFat: { min: 0, max: 80 },
  neck: { min: 5, max: 300 },
  chest: { min: 5, max: 300 },
  biceps: { min: 5, max: 300 },
  waist: { min: 5, max: 300 },
  hips: { min: 5, max: 300 },
  thigh: { min: 5, max: 300 },
  calf: { min: 5, max: 300 },
};

/**
 * A MEASUREMENT on the request — every field but the two that are not measurements. Naming them by
 * exclusion off the generated type is what makes a typo in `WIRE_FIELD` a compile error rather than
 * a number the server silently drops.
 */
type MeasurementWireField = Exclude<keyof PostBodyMeasurementRequest, 'source' | 'recordedAt'>;

/** The wire field each form field writes. */
const WIRE_FIELD: Readonly<Record<MeasurementFormField, MeasurementWireField>> = {
  weight: 'weightKg',
  bodyFat: 'bodyFatPercentage',
  neck: 'neckCm',
  chest: 'chestCm',
  biceps: 'bicepCm',
  waist: 'waistCm',
  hips: 'hipCm',
  thigh: 'thighCm',
  calf: 'calfCm',
};

/**
 * The unit each form field is measured in, as a locale KEY.
 *
 * The form's own field names are not the wire's — `hips` is `hipCm`, `biceps` is `bicepCm` — and the
 * units are already decided per WIRE field for the chart and the tiles, so this walks through that
 * map rather than growing a second table beside it. The one thing that would drift here is exactly
 * the thing that must not: the tile above the form saying `cm` while the field under it says
 * nothing.
 */
export function measurementFieldUnitKey(field: MeasurementFormField): string {
  return measurementUnitKey(WIRE_FIELD[field]);
}

/**
 * The label a FORM field wears, walked through the same wire map for the same reason the units are:
 * the drawer prints „Tkanka tłuszczowa" and the copied form table abbreviates it, and one
 * abbreviation living in two places is how the drawer and the table end up disagreeing.
 */
export function measurementFieldLabelKey(field: MeasurementFormField): string {
  return WEB_LABEL_OVERRIDE.has(WIRE_FIELD[field])
    ? measurementLabelKey(WIRE_FIELD[field])
    : `clients.detail.body.fields.${field}`;
}

/**
 * The bound this field is actually checked against, for the message under a refused input.
 *
 * "Wartość poza zakresem" told the trainer they were wrong without telling them what right is, and
 * the three kinds of bound here are genuinely different — 20 to 500 kg, 0 to 80%, 5 to 300 cm. The
 * message is composed from THIS table rather than typed into the locale, so a bound that moves with
 * the backend moves in the sentence too.
 */
export function measurementBounds(field: MeasurementFormField): MeasurementBounds {
  return BOUNDS[field];
}

/**
 * A typed decimal, Polish or English.
 *
 * A Polish keyboard produces `64,2` and the trainer will type it. Rejecting the comma would make
 * the field refuse the number the trainer can see on their scale — so EVERY comma is normalised, not
 * just the first. `'1,234,5'.replace(',', '.')` left `1.234,5`, which happened to be `NaN` anyway;
 * the global replace makes the rejection deliberate rather than lucky.
 *
 * ── The shape is checked, and `Number` alone is not a check ──────────────────────────────────
 * `Number('0x10')` is 16 and `Number('1e3')` is 1000, so a trainer typing either got a value the
 * form silently accepted and sent. The pattern is what a measurement can actually look like: digits,
 * one separator, digits.
 *
 * ── Blank and unparsable are DIFFERENT, and that is the caller's problem ─────────────────────
 * Both come back `undefined`, because a parser has nothing to say about which. `buildMeasurementRequest`
 * tells them apart by looking at the raw string, which is the only place the distinction means
 * anything: blank is legal on every optional field, and unparsable never is.
 */
const DECIMAL = /^\d+(?:[.]\d+)?$/;

export function parseDecimal(raw: string): number | undefined {
  const trimmed = raw.trim().replaceAll(',', '.');
  if (!DECIMAL.test(trimmed)) return undefined;
  const value = Number(trimmed);
  return Number.isFinite(value) ? value : undefined;
}

/**
 * What this app POSTs, which is **the generated request type and not a hand-written cousin of it.**
 *
 * It was an interface here with an index signature, handed to the mutation through
 * `as unknown as` — so a typo in `WIRE_FIELD` would have type-checked and shipped a measurement the
 * backend ignores. Narrowed to the two fields this form always sends: `weightKg` is required on the
 * wire and `source` is stamped rather than typed, so both are non-optional here while every girth
 * stays optional exactly as the contract has it. No wire change: the shape is the contract's own.
 */
export type MeasurementRequest = PostBodyMeasurementRequest &
  Readonly<{ weightKg: number; source: string }>;

export interface MeasurementSubmission {
  /** `null` when something refused. The caller sends nothing and paints `invalid`. */
  readonly request: MeasurementRequest | null;
  readonly invalid: readonly MeasurementFormField[];
}

/**
 * Turn the form into a request, or name the fields that stopped it.
 *
 * `source` is stamped here as `TrainerMeasure` and is not a form field — the backend forces a
 * customer's own submission to `SelfReport` regardless of what is sent, so the value is only ever
 * meaningful from a trainer, and letting it be typed would invite a value the server ignores.
 */
export function buildMeasurementRequest(values: MeasurementFormValues): MeasurementSubmission {
  const invalid: MeasurementFormField[] = [];
  const request: Partial<Record<MeasurementWireField, number>> = {};

  for (const field of MEASUREMENT_FORM_FIELDS) {
    const raw = values[field].trim();
    if (raw === '') {
      // Weight is the only required field. Everything else may be blank, and blank is not invalid.
      if (field === 'weight') invalid.push(field);
      continue;
    }
    const parsed = parseDecimal(raw);
    if (parsed === undefined) {
      // Typed and unreadable, which is NOT the same as left empty — and it used to take the same
      // branch, so `72,,5` in the waist field vanished on save and the snapshot came back without a
      // waist. The trainer's only clue was a number that was not there any more.
      invalid.push(field);
      continue;
    }
    const bound = BOUNDS[field];
    if (parsed < bound.min || parsed > bound.max) {
      invalid.push(field);
      continue;
    }
    request[WIRE_FIELD[field]] = parsed;
  }

  const weightKg = request.weightKg;
  if (invalid.length > 0 || weightKg === undefined) {
    return { request: null, invalid: invalid.length > 0 ? invalid : ['weight'] };
  }
  // The narrowing is a real one — `weightKg` has just been proved present — so no cast is needed
  // and a field name that does not exist on the contract cannot compile.
  return { request: { ...request, weightKg, source: TRAINER_SOURCE }, invalid: [] };
}
